import { PayoffPath, ResultStage, StudentLoanPlanResult } from 'api/StudentLoanApi';
import { addMonths, formatMonthYear } from 'utils/dateUtils';
import { formatMonetaryAmount } from 'utils/formatMonetaryAmount';

export const STUDENT_AID_URL = 'https://studentaid.gov/fsa-id/sign-in/landing';

const PAYMENT_ROUNDING_DOLLARS = 5;
const SAVINGS_ROUNDING_DOLLARS = 100;
const MIN_SAVINGS_TO_SHOW_DOLLARS = 100;
const MIN_RANGE_SPREAD_DOLLARS = 1000;

export interface StudentLoanRow {
  label: string;
  value: string;
  isStrong?: boolean;
}

const roundTo = (value: number, step: number) => Math.round(value / step) * step;

/** Reads a number out of a formatted input value such as "$62,000". Empty or invalid input is 0. */
export const parseAmount = (value: string): number => Number(value.replace(/[^0-9.]/g, '')) || 0;

export const toInputValue = (amount: number | undefined): string => (amount === undefined ? '' : String(amount));

export const formatPayment = (amount: number) => formatMonetaryAmount(roundTo(amount, PAYMENT_ROUNDING_DOLLARS));

export const formatRoundedAmount = (amount: number) => formatMonetaryAmount(roundTo(amount, SAVINGS_ROUNDING_DOLLARS));

export const formatMonthsFromNow = (months: number) => formatMonthYear(addMonths(new Date(), months));

/** A single figure at the Plan stage, a range at the Estimate stage when the spread is meaningful. Null hides the headline. */
export const getSavingsLabel = ({ stage, savings }: StudentLoanPlanResult): string | null => {
  if (!savings) return null;
  if (stage === ResultStage.Plan) {
    return savings.low >= MIN_SAVINGS_TO_SHOW_DOLLARS ? formatRoundedAmount(savings.low) : null;
  }
  if (savings.high < MIN_SAVINGS_TO_SHOW_DOLLARS) return null;

  const low = formatRoundedAmount(Math.max(0, savings.low));
  return savings.high - savings.low >= MIN_RANGE_SPREAD_DOLLARS ? `${low}–${formatRoundedAmount(savings.high)}` : low;
};

export const getPathSummaryRows = (path: PayoffPath): StudentLoanRow[] => {
  const rows: StudentLoanRow[] = [{ label: 'Plan', value: path.label }];

  if (path.pslf) {
    rows.push(
      { label: 'Payments left', value: String(path.monthsToDone) },
      { label: 'Forgiveness date', value: formatMonthsFromNow(path.monthsToDone) },
      { label: 'Forgiven, tax-free', value: formatRoundedAmount(path.forgivenPslf) },
    );
  } else if (path.forgivenIdr > 0) {
    rows.push(
      { label: 'Forgiveness date', value: formatMonthsFromNow(path.monthsToDone) },
      { label: 'Est. tax on forgiveness', value: formatRoundedAmount(path.taxPlaceholder) },
    );
  } else {
    rows.push({ label: 'Paid off', value: formatMonthsFromNow(path.monthsToDone) });
  }

  return rows;
};

export const getComparisonRows = ({ current, recommended }: StudentLoanPlanResult): StudentLoanRow[] => [
  {
    label: 'Monthly payment',
    value: `${formatPayment(current.firstMonthlyPayment)} → ${formatPayment(recommended.firstMonthlyPayment)}`,
  },
  {
    label: 'Total you pay',
    value: `${formatRoundedAmount(current.totalCost)} → ${formatRoundedAmount(recommended.totalCost)}`,
    isStrong: true,
  },
  {
    label: 'Done by',
    value: `${formatMonthsFromNow(current.monthsToDone)} → ${formatMonthsFromNow(recommended.monthsToDone)}`,
  },
];
